import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDriverDeliveryGroups } from "api/deliveries";
import { Delivery, DeliveryGroup } from "typings/models";
import {
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Chip,
    Button,
    Divider,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import { useTravelContext } from "misc/TravelContext";
import { formatDate } from 'misc/dateUtils';

const statusLabel = (status: DeliveryGroup['status']) => {
    switch (status) {
        case 'Completed': return { label: 'Completada', color: 'success' as const, icon: <CheckCircleIcon fontSize="small" /> };
        case 'In Progress': return { label: 'En progreso', color: 'warning' as const, icon: <PendingIcon fontSize="small" /> };
        case 'Failed': return { label: 'Con problemas', color: 'error' as const, icon: <ErrorIcon fontSize="small" /> };
        default: return { label: status, color: 'default' as const, icon: null };
    }
};

const deliveryStatusLabel = (status: Delivery['status']) => {
    switch (status) {
        case 'Completed': return { label: 'Entregado', color: 'success' as const };
        case 'In Progress': return { label: 'En progreso', color: 'warning' as const };
        case 'Failed': return { label: 'Problema', color: 'error' as const };
        case 'Cancelled': return { label: 'Cancelado', color: 'default' as const };
        default: return { label: status, color: 'default' as const };
    }
};

const DeliveredRoutesPage: React.FC = () => {
    const navigate = useNavigate();
    const [deliveryGroups, setDeliveryGroups] = useState<DeliveryGroup[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { setDeliveryGroup } = useTravelContext();

    useEffect(() => {
        getDriverDeliveryGroups().then((groups) => {
            setDeliveryGroups(groups);
            setLoading(false);
        });
    }, []);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    if (deliveryGroups.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center', mt: 4 }}>
                <Typography color="text.secondary">No tienes rutas asignadas.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            {deliveryGroups.map((group) => {
                const total = group.deliveries.length;
                const done = group.deliveries.filter(d => d.status === 'Completed' || d.status === 'Failed').length;
                const allDone = total > 0 && done === total;
                const effectiveStatus = allDone ? 'Completed' : group.status;
                const st = statusLabel(effectiveStatus);
                const isCompleted = effectiveStatus === 'Completed';

                return (
                    <Accordion key={group.id} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%', minWidth: 0, overflow: 'hidden', pr: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography fontWeight="bold">
                                        {formatDate(group.scheduled_date ?? group.created_at)}
                                    </Typography>
                                    <Chip
                                        label={st.label}
                                        color={st.color}
                                        size="small"
                                        icon={st.icon ?? undefined}
                                    />
                                    <Typography variant="body2" color="text.secondary">
                                        {done}/{total} entregas
                                    </Typography>
                                </Box>
                                <Box>
                                    {group.deliveries.map((d, i) => (
                                        <Typography key={i} variant="caption" color="text.secondary" display="block">
                                            · {d.order.client.name}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ p: 0 }}>
                            <Box sx={{ px: 2, pb: 1, pt: 0.5 }}>
                                <Button
                                    variant={isCompleted ? 'outlined' : 'contained'}
                                    size="small"
                                    onClick={() => {
                                        setDeliveryGroup(group);
                                        navigate("/delivery");
                                    }}
                                >
                                    {isCompleted ? 'Ver detalles' : 'Continuar ruta'}
                                </Button>
                            </Box>
                            <Divider />
                            {group.deliveries.map((delivery, i) => {
                                const ds = deliveryStatusLabel(delivery.status);
                                return (
                                    <Box key={delivery.id}>
                                        {i > 0 && <Divider />}
                                        <Box sx={{ px: 2, py: 1.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {delivery.order.id}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {delivery.order.client.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {delivery.order.client.address}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Chip label={ds.label} color={ds.color} size="small" />
                                                    {delivery.completed_at && (
                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                            {formatDate(delivery.completed_at)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

export default DeliveredRoutesPage;
